import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PeoplePicker from './PeoplePicker';
import { SortHeader, SortMenu, Sheet, nextSort, usePhone } from './ListSort';
import { downloadCsv } from '../lib/exportTable';
import { hours, hoursDelta } from '../lib/hours';
import { money, rate } from '../lib/money';
import './InvoicingView.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * INVOICING: the invoices, and what each one covers.
 *
 * Gordon, 23 October: "create a new view for invoicing where I can see the
 * invoicing list (filter and sort by contractor) and when I click on any
 * invoice the tickets expand out below the invoice showing me the budget,
 * hours worked, hours billed and expenses billed by the contractor on that
 * ticket."
 *
 * An invoice here is the contractor's own: they raise it outside Jarvis and
 * stamp the approved tickets it covers with its reference in their portal.
 * Every figure is defined once, in backend lib/invoicing.js, and arrives
 * computed - this page adds nothing up itself except to render it.
 *
 *   budget        the hour budget set when the ticket was approved
 *   worked        the sessions THIS contractor logged on the ticket
 *   approved      what the approver signed off
 *   billed        the hours on the invoice
 *   expenses      reimbursements billed on the invoice, at cost
 *   variance      billed - budget; above zero is over budget
 *
 * Two homes, one component. The office's is Ticket Mgmt > Reporting >
 * Invoicing (`contractorId` absent): every contractor, filterable by them,
 * with the view held in the URL so it can be bookmarked and sent. A
 * contractor's is a tab of their own portal (`contractorId` set): the same
 * page over their invoices only, the server scoping it before any filter.
 *
 * Several invoices may be open at once, each loading its lines the first time
 * it opens and keeping them. A ticket opens in a new tab, so the place in the
 * list is not lost.
 */

const SORTS = [
  { key: 'date', label: 'Date', dir: 'desc' },
  { key: 'invoice', label: 'Invoice' },
  { key: 'contractor', label: 'Contractor' },
  { key: 'period', label: 'Period', dir: 'desc' },
  { key: 'status', label: 'Status' },
  { key: 'tickets', label: 'Tickets', dir: 'desc' },
  { key: 'budget', label: 'Budget', dir: 'desc' },
  { key: 'hours_worked', label: 'Hours worked', dir: 'desc' },
  { key: 'hours_billed', label: 'Hours billed', dir: 'desc' },
  { key: 'expenses', label: 'Expenses', dir: 'desc' },
  { key: 'amount', label: 'Amount', dir: 'desc' }
];
const FIRST_DIR = Object.fromEntries(SORTS.map((s) => [s.key, s.dir || 'asc']));
const SORT_KEYS = SORTS.map((s) => s.key);
const STATUS_LABEL = { pending: 'Pending', invoiced: 'Invoiced', paid: 'Paid',
  disputed: 'Disputed', mixed: 'Mixed' };
const FILTERS = ['q', 'contractor', 'status', 'from', 'to'];

const dash = '—';
const maybeH = (v) => (v === null || v === undefined ? dash : hours(v));
const day = (v) => {
  if (!v) return dash;
  const d = new Date(`${String(v).slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? String(v)
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const period = (a, b) => {
  if (!a && !b) return dash;
  if (!b || String(a).slice(0, 10) === String(b).slice(0, 10)) return day(a || b);
  const A = new Date(`${String(a).slice(0, 10)}T12:00:00Z`);
  const B = new Date(`${String(b).slice(0, 10)}T12:00:00Z`);
  const sameYear = A.getUTCFullYear() === B.getUTCFullYear();
  const short = A.toLocaleDateString('en-GB', { day: 'numeric', month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }) });
  return `${short} – ${day(b)}`;
};

/**
 * The view state - in the URL for the office page, in memory for the portal
 * tab (whose address belongs to the portal). Same shape either way.
 */
function useViewParams(inUrl) {
  const [urlParams, setUrlParams] = useSearchParams();
  const [local, setLocal] = useState(() => new URLSearchParams());
  const params = inUrl ? urlParams : local;
  const set = useCallback((fn) => {
    if (inUrl) setUrlParams((prev) => fn(new URLSearchParams(prev)), { replace: true });
    else setLocal((prev) => fn(new URLSearchParams(prev)));
  }, [inUrl, setUrlParams]);
  return [params, set];
}

function readView(params) {
  const sort = SORT_KEYS.includes(params.get('sort')) ? params.get('sort') : 'date';
  const d = params.get('dir');
  return {
    q: params.get('q') || '',
    contractor: (params.get('contractor') || '').split(',').filter(Boolean),
    status: params.get('status') || 'all',
    from: params.get('from') || '',
    to: params.get('to') || '',
    sort,
    dir: d === 'asc' || d === 'desc' ? d : FIRST_DIR[sort],
    page: Math.max(1, parseInt(params.get('page'), 10) || 1)
  };
}

export default function InvoicingView({ contractorId = null, token = null,
  ticketHref = (id) => `/tickets/${id}` }) {
  const scoped = Boolean(contractorId);
  // A contractor's portal token, when there is one; an administrator
  // previewing the portal goes with their own sign-in, as Issued Bills does.
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : undefined), [token]);
  const listUrl = scoped ? `${API_BASE}/contractors/${contractorId}/invoicing`
    : `${API_BASE}/billing/invoicing`;
  const linesUrl = scoped ? `${API_BASE}/contractors/${contractorId}/invoicing/tickets`
    : `${API_BASE}/billing/invoicing/tickets`;

  const [params, setParams] = useViewParams(!scoped);
  const view = useMemo(() => readView(params), [params]);
  const phone = usePhone(720);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [lines, setLines] = useState({});   // key -> { loading, error, body }
  const [search, setSearch] = useState(view.q);
  const [sheet, setSheet] = useState(false);
  const [exporting, setExporting] = useState(false);
  const optionsRef = useRef(null);

  const update = useCallback((patch, { keepPage = false } = {}) => {
    setParams((next) => {
      for (const [k, v] of Object.entries(patch)) {
        const empty = v === undefined || v === null || v === '' || v === 'all'
          || (Array.isArray(v) && v.length === 0) || (k === 'page' && Number(v) === 1);
        if (empty) next.delete(k);
        else next.set(k, Array.isArray(v) ? v.join(',') : String(v));
      }
      if (!keepPage && !('page' in patch)) next.delete('page');
      return next;
    });
  }, [setParams]);

  useEffect(() => { setSearch(view.q); }, [view.q]);
  useEffect(() => {
    if (search === view.q) return undefined;
    const id = setTimeout(() => update({ q: search.trim() }), 300);
    return () => clearTimeout(id);
  }, [search, view.q, update]);

  const query = useMemo(() => {
    const p = { sort: view.sort, dir: view.dir, page: view.page, size: 25 };
    if (view.q) p.q = view.q;
    if (view.contractor.length) p.contractor = view.contractor.join(',');
    if (view.status !== 'all') p.status = view.status;
    if (view.from) p.from = view.from;
    if (view.to) p.to = view.to;
    return p;
  }, [view]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios.get(listUrl, { params: query, headers })
      .then((r) => {
        if (!live) return;
        setData(r.data);
        if (r.data?.options) optionsRef.current = r.data.options;
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        setError(err.response?.data?.error || 'Could not load the invoices');
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [listUrl, query, headers]);

  const loadLines = useCallback((inv) => {
    setLines((m) => ({ ...m, [inv.key]: { ...(m[inv.key] || {}), loading: true, error: null } }));
    const p = scoped ? { invoice: inv.invoice_number }
      : { contractor: inv.contractor_id, invoice: inv.invoice_number };
    axios.get(linesUrl, { params: p, headers })
      .then((r) => setLines((m) => ({ ...m, [inv.key]: { loading: false, body: r.data } })))
      .catch((err) => setLines((m) => ({ ...m,
        [inv.key]: { loading: false, error: err.response?.data?.error || 'Could not load the tickets on this invoice' } })));
  }, [linesUrl, scoped, headers]);

  const toggle = (inv) => {
    const opening = !open.has(inv.key);
    setOpen((prev) => {
      const next = new Set(prev);
      if (opening) next.add(inv.key); else next.delete(inv.key);
      return next;
    });
    if (opening && !lines[inv.key]?.body && !lines[inv.key]?.loading) loadLines(inv);
  };

  const invoices = data?.invoices || [];
  const totals = data?.totals || null;
  const options = data?.options || optionsRef.current || { contractors: [], statuses: [] };
  const pages = data?.pages || 1;
  const narrowed = FILTERS.filter((k) => (Array.isArray(view[k]) ? view[k].length : view[k] && view[k] !== 'all'));
  const allOpen = invoices.length > 0 && invoices.every((i) => open.has(i.key));

  useEffect(() => {
    if (data && view.page > pages) update({ page: pages }, { keepPage: true });
  }, [data, view.page, pages, update]);

  const onSort = (key) => update(nextSort(view, key, FIRST_DIR));
  const clear = () => {
    setSearch('');
    update({ q: null, contractor: null, status: null, from: null, to: null });
  };

  const expandAll = () => {
    if (allOpen) { setOpen(new Set()); return; }
    const next = new Set(open);
    for (const inv of invoices) {
      next.add(inv.key);
      if (!lines[inv.key]?.body && !lines[inv.key]?.loading) loadLines(inv);
    }
    setOpen(next);
  };

  // The whole filtered list, not just this page - what is downloaded is
  // what the filters describe.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(listUrl, { params: { ...query, page: 1, size: 'all' }, headers });
      const rows = (r.data?.invoices || []).map((i) => ({
        ...i, status: STATUS_LABEL[i.status] || i.status
      }));
      const cols = [
        { key: 'invoice_number', label: 'Invoice' },
        ...(scoped ? [] : [{ key: 'contractor_name', label: 'Contractor' }, { key: 'company', label: 'Company' }]),
        { key: 'invoice_date', label: 'Date' },
        { key: 'period_start', label: 'Period from' },
        { key: 'period_end', label: 'Period to' },
        { key: 'status', label: 'Status' },
        { key: 'ticket_count', label: 'Tickets', type: 'number' },
        { key: 'budget_hours', label: 'Budget (h)', type: 'number' },
        { key: 'hours_worked', label: 'Hours worked', type: 'number' },
        { key: 'hours_billed', label: 'Hours billed', type: 'number' },
        { key: 'labour', label: 'Labour', type: 'money' },
        { key: 'expenses', label: 'Expenses', type: 'money' },
        { key: 'amount', label: 'Amount', type: 'money' },
        { key: 'over_budget', label: 'Tickets over budget', type: 'number' }
      ];
      downloadCsv(cols, rows, `invoicing-${new Date().toISOString().slice(0, 10)}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not prepare the download');
    } finally {
      setExporting(false);
    }
  };

  const contractorPicker = (inline) => (!scoped && (
    <PeoplePicker
      label="Contractor" idBase="inv-contractor" inline={inline} allLabel="All contractors"
      options={options.contractors || []} value={view.contractor}
      onChange={(v) => update({ contractor: v })}
    />
  ));

  const statusSelect = (cls) => (
    <select className={cls} value={view.status} aria-label="Filter by status"
            data-filter="status" onChange={(e) => update({ status: e.target.value })}>
      <option value="all">Any status</option>
      {(options.statuses || []).map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
    </select>
  );

  const dates = (
    <span className="iv-dates" role="group" aria-label="Invoice date">
      <label>
        <span>From</span>
        <input type="date" value={view.from} max={view.to || undefined}
               onChange={(e) => update({ from: e.target.value })} aria-label="Invoices from" />
      </label>
      <label>
        <span>to</span>
        <input type="date" value={view.to} min={view.from || undefined}
               onChange={(e) => update({ to: e.target.value })} aria-label="Invoices to" />
      </label>
    </span>
  );

  /* ---------------------------------------------------------- the lines */
  const linesFor = (inv) => {
    const st = lines[inv.key] || { loading: true };
    if (st.error) {
      return (
        <div className="iv-lines-msg" role="alert">
          {st.error}{' '}
          <button type="button" className="iv-link-btn" onClick={() => loadLines(inv)}>Try again</button>
        </div>
      );
    }
    if (!st.body) return <div className="iv-lines-msg">Loading the tickets…</div>;
    const { tickets, totals: T, known } = st.body;

    const variance = (t) => {
      if (t.budget_variance === null || t.budget_variance === undefined) {
        return <span className="iv-quiet" title="No budget was set for this ticket">no budget</span>;
      }
      if (t.over_budget) {
        return <span className="iv-over" title={`${hours(t.budget_variance)} over the ${hours(t.budget_hours)} budget`}>
          {hoursDelta(t.budget_variance)}h<span className="iv-over-tag">over</span></span>;
      }
      return <span className="iv-under">{t.budget_variance === 0 ? 'on budget' : `${hoursDelta(t.budget_variance)}h`}</span>;
    };

    const link = (t) => (t.ticket_id
      ? <a className="iv-ticket" href={ticketHref(t.ticket_id)} target="_blank" rel="noreferrer">{t.ticket_number}</a>
      : <span className="iv-quiet" title="This ticket no longer exists; its money still counts">no ticket</span>);

    if (phone) {
      return (
        <ul className="iv-line-cards">
          {tickets.map((t) => (
            <li key={t.billing_id} className={t.over_budget ? 'iv-line-card is-over' : 'iv-line-card'}>
              <div className="iv-line-head">{link(t)} <span>{t.ticket_title || dash}</span></div>
              <dl>
                <div><dt>Budget</dt><dd>{maybeH(t.budget_hours)}</dd></div>
                <div><dt>Worked</dt><dd className={t.worked_over_budget ? 'iv-warn' : undefined}>{maybeH(t.hours_worked)}</dd></div>
                <div><dt>Billed</dt><dd>{hours(t.hours_billed)}</dd></div>
                <div><dt>Expenses</dt><dd>{t.expenses > 0 ? money(t.expenses) : dash}</dd></div>
                <div><dt>Amount</dt><dd>{money(t.amount)}</dd></div>
                <div><dt>vs budget</dt><dd>{variance(t)}</dd></div>
              </dl>
            </li>
          ))}
          <li className="iv-line-card is-total">
            <div className="iv-line-head"><strong>Total</strong> <span>{T.tickets} ticket{T.tickets === 1 ? '' : 's'}</span></div>
            <dl>
              <div><dt>Budget</dt><dd>{maybeH(T.budget_hours)}</dd></div>
              <div><dt>Worked</dt><dd>{maybeH(T.hours_worked)}</dd></div>
              <div><dt>Billed</dt><dd>{hours(T.hours_billed)}</dd></div>
              <div><dt>Expenses</dt><dd>{T.expenses > 0 ? money(T.expenses) : dash}</dd></div>
              <div><dt>Amount</dt><dd>{money(T.amount)}</dd></div>
              <div><dt>vs budget</dt><dd>{T.budget_variance === null ? dash
                : <span className={T.budget_variance > 0.004 ? 'iv-over' : 'iv-under'}>{hoursDelta(T.budget_variance)}h</span>}</dd></div>
            </dl>
          </li>
        </ul>
      );
    }

    return (
      <table className="iv-lines" aria-label={`Tickets on ${inv.invoice_number}`}>
        <thead>
          <tr>
            <th>Ticket</th>
            <th className="num" title="Hours budgeted when the ticket was approved">Budget</th>
            <th className="num" title={`Sessions ${scoped ? 'you' : inv.contractor_name || 'this contractor'} logged on the ticket`}>Hours worked</th>
            {known?.hours_approved && <th className="num" title="Hours the approver signed off">Approved</th>}
            <th className="num" title="Hours on the invoice">Hours billed</th>
            <th className="num">Rate</th>
            <th className="num" title="Reimbursements billed on the invoice">Expenses billed</th>
            <th className="num">Amount</th>
            <th className="num" title="Hours billed against the budget">vs budget</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr key={t.billing_id} className={t.over_budget ? 'is-over' : undefined}>
              <td className="iv-ticket-cell">
                {link(t)}
                <span className="iv-title">{t.ticket_title || ''}</span>
              </td>
              <td className="num">
                {maybeH(t.budget_hours)}
                {t.budget_amount !== null && <small>{money(t.budget_amount)}</small>}
              </td>
              <td className={t.worked_over_budget ? 'num iv-warn' : 'num'}
                  title={t.worked_over_budget ? 'More hours were worked than budgeted' : undefined}>
                {maybeH(t.hours_worked)}
                {Number.isFinite(t.sessions) && t.sessions > 0 && <small>{t.sessions} session{t.sessions === 1 ? '' : 's'}</small>}
              </td>
              {known?.hours_approved && <td className="num">{maybeH(t.hours_approved)}</td>}
              <td className="num iv-strong">{hours(t.hours_billed)}</td>
              <td className="num">{rate(t.hourly_rate)}</td>
              <td className="num">{t.expenses > 0 ? money(t.expenses) : dash}</td>
              <td className="num iv-strong">{money(t.amount)}</td>
              <td className="num">{variance(t)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total · {T.tickets} ticket{T.tickets === 1 ? '' : 's'}
              {T.budgeted < T.tickets && T.budgeted > 0 && <small>{T.budgeted} with a budget</small>}
            </th>
            <td className="num">{maybeH(T.budget_hours)}</td>
            <td className="num">{maybeH(T.hours_worked)}</td>
            {known?.hours_approved && <td className="num">{maybeH(T.hours_approved)}</td>}
            <td className="num">{hours(T.hours_billed)}</td>
            <td className="num" />
            <td className="num">{T.expenses > 0 ? money(T.expenses) : dash}</td>
            <td className="num">{money(T.amount)}</td>
            <td className="num">{T.budget_variance === null ? dash
              : <span className={T.budget_variance > 0.004 ? 'iv-over' : 'iv-under'}>
                {T.budget_variance === 0 ? 'on budget' : `${hoursDelta(T.budget_variance)}h`}</span>}</td>
          </tr>
        </tfoot>
      </table>
    );
  };

  const status = (s) => <span className="iv-status" data-status={s}>{STATUS_LABEL[s] || s}</span>;
  const budgetCell = (inv) => (
    <>
      {maybeH(inv.budget_hours)}
      {inv.over_budget > 0 && (
        <span className="iv-over-chip" title={`${inv.over_budget} ticket${inv.over_budget === 1 ? '' : 's'} billed over budget`}>
          {inv.over_budget} over
        </span>
      )}
    </>
  );

  const cols = scoped ? 11 : 12;

  return (
    <div className="iv" data-scoped={String(scoped)}>
      <div className="iv-tools">
        <input type="search" className="iv-search" value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder={scoped ? 'Find an invoice or ticket number…' : 'Find an invoice, contractor or ticket…'}
               aria-label="Search invoices" />
        {phone ? (
          <>
            <button type="button" className="ls-filter-btn" onClick={() => setSheet(true)} aria-haspopup="dialog">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" /></svg>
              Filters
              {narrowed.filter((k) => k !== 'q').length > 0
                && <span className="ls-count">{narrowed.filter((k) => k !== 'q').length}</span>}
            </button>
            <SortMenu options={scoped ? SORTS.filter((s) => s.key !== 'contractor') : SORTS}
                      sort={view.sort} dir={view.dir} onChange={(s) => update(s)} />
          </>
        ) : (
          <>
            {contractorPicker(false)}
            {statusSelect('iv-select')}
            {dates}
            {narrowed.length > 0 && <button type="button" className="iv-btn" onClick={clear}>Clear</button>}
          </>
        )}
        <span className="iv-actions">
          {invoices.length > 0 && (
            <button type="button" className="iv-btn" onClick={expandAll} aria-pressed={allOpen}>
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          )}
          <button type="button" className="iv-btn" onClick={exportCsv} disabled={exporting || !invoices.length}>
            {exporting ? 'Preparing…' : 'Download CSV'}
          </button>
          <button type="button" className="iv-btn" onClick={() => window.print()} disabled={!invoices.length}>
            Print
          </button>
        </span>
      </div>

      <Sheet title="Filter invoices" open={sheet} onClose={() => setSheet(false)}
             footer={(
               <>
                 <button type="button" className="ls-btn" onClick={clear} disabled={!narrowed.length}>Clear all</button>
                 <button type="button" className="ls-btn is-primary" onClick={() => setSheet(false)}>
                   Show {data?.total ?? 0} invoice{data?.total === 1 ? '' : 's'}
                 </button>
               </>
             )}>
        {contractorPicker(true)}
        <label className="ls-field">Status{statusSelect('')}</label>
        <label className="ls-field">From
          <input type="date" value={view.from} max={view.to || undefined}
                 onChange={(e) => update({ from: e.target.value })} />
        </label>
        <label className="ls-field">To
          <input type="date" value={view.to} min={view.from || undefined}
                 onChange={(e) => update({ to: e.target.value })} />
        </label>
      </Sheet>

      {error && <p className="iv-error" role="alert">{error}</p>}

      {totals && totals.invoices > 0 && (
        <div className="iv-figures" role="status" aria-live="polite">
          <span><strong>{totals.invoices}</strong> invoice{totals.invoices === 1 ? '' : 's'}</span>
          <span><strong>{totals.tickets}</strong> ticket{totals.tickets === 1 ? '' : 's'}</span>
          <span><strong>{maybeH(totals.hours_worked)}</strong> worked</span>
          <span><strong>{hours(totals.hours_billed)}</strong> billed</span>
          {totals.expenses > 0 && <span><strong>{money(totals.expenses)}</strong> expenses</span>}
          {totals.over_budget > 0 && (
            <span className="iv-figure-over"><strong>{totals.over_budget}</strong> ticket{totals.over_budget === 1 ? '' : 's'} over budget</span>
          )}
          <span className="iv-figure-amount"><strong>{money(totals.amount)}</strong></span>
        </div>
      )}

      {!loading && invoices.length === 0 && !error && (
        <p className="iv-quiet iv-empty">
          {narrowed.length
            ? 'No invoices match those filters.'
            : scoped
              ? 'You have not put anything on an invoice yet. Once you give approved work an invoice number under Billing, the invoice appears here with its tickets.'
              : 'Nothing has been invoiced yet. An invoice appears here once a contractor gives approved work an invoice number in their portal.'}
        </p>
      )}

      {invoices.length > 0 && !phone && (
        <div className="iv-table-wrap">
          <table className="iv-table" aria-busy={loading}>
            <thead>
              <tr>
                <th className="iv-chev-col"><span className="iv-sr">Open</span></th>
                <SortHeader label="Invoice" sortKey="invoice" {...view} onSort={onSort} />
                {!scoped && <SortHeader label="Contractor" sortKey="contractor" {...view} onSort={onSort} />}
                <SortHeader label="Date" sortKey="date" {...view} onSort={onSort} />
                <SortHeader label="Period" sortKey="period" {...view} onSort={onSort} />
                <SortHeader label="Status" sortKey="status" {...view} onSort={onSort} />
                <SortHeader label="Tickets" sortKey="tickets" className="num" {...view} onSort={onSort} />
                <SortHeader label="Budget" sortKey="budget" className="num" {...view} onSort={onSort} />
                <SortHeader label="Worked" sortKey="hours_worked" className="num" {...view} onSort={onSort} />
                <SortHeader label="Billed" sortKey="hours_billed" className="num" {...view} onSort={onSort} />
                <SortHeader label="Expenses" sortKey="expenses" className="num" {...view} onSort={onSort} />
                <SortHeader label="Amount" sortKey="amount" className="num" {...view} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const isOpen = open.has(inv.key);
                const id = `iv-lines-${inv.key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                return (
                  <React.Fragment key={inv.key}>
                    <tr className={isOpen ? 'iv-row is-open' : 'iv-row'} data-key={inv.key}
                        onClick={(e) => { if (!e.target.closest('a, button')) toggle(inv); }}>
                      <td className="iv-chev-col">
                        <button type="button" className="iv-expand" aria-expanded={isOpen}
                                aria-controls={id} onClick={() => toggle(inv)}>
                          <span className="iv-sr">
                            {isOpen ? 'Hide' : 'Show'} the tickets on {inv.invoice_number}
                            {scoped ? '' : ` from ${inv.contractor_name || 'unknown'}`}
                          </span>
                          <svg className="iv-chev" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                               strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M6 4 10 8 6 12" />
                          </svg>
                        </button>
                      </td>
                      <td className="iv-no">{inv.invoice_number}</td>
                      {!scoped && (
                        <td>
                          <span className="iv-contractor">{inv.contractor_name || dash}</span>
                          {inv.company && <small className="iv-sub">{inv.company}</small>}
                        </td>
                      )}
                      <td className="iv-date">{day(inv.invoice_date)}</td>
                      <td className="iv-date">{period(inv.period_start, inv.period_end)}</td>
                      <td>{status(inv.status)}</td>
                      <td className="num">{inv.ticket_count}</td>
                      <td className="num">{budgetCell(inv)}</td>
                      <td className="num">{maybeH(inv.hours_worked)}</td>
                      <td className="num iv-strong">{hours(inv.hours_billed)}</td>
                      <td className="num">{inv.expenses > 0 ? money(inv.expenses) : dash}</td>
                      <td className="num iv-strong">{money(inv.amount)}</td>
                    </tr>
                    <tr className={isOpen ? 'iv-detail is-open' : 'iv-detail'} aria-hidden={!isOpen}>
                      <td colSpan={cols}>
                        <div className="iv-collapse" id={id} role="region"
                             aria-label={`Tickets on ${inv.invoice_number}`}
                             {...(isOpen ? {} : { inert: '' })}>
                          <div className="iv-collapse-inner">
                            {(isOpen || lines[inv.key]) && linesFor(inv)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {invoices.length > 0 && phone && (
        <ul className="iv-cards" aria-busy={loading}>
          {invoices.map((inv) => {
            const isOpen = open.has(inv.key);
            const id = `iv-card-${inv.key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
            return (
              <li key={inv.key} className={isOpen ? 'iv-card is-open' : 'iv-card'} data-key={inv.key}>
                <button type="button" className="iv-card-head" aria-expanded={isOpen}
                        aria-controls={id} onClick={() => toggle(inv)}>
                  <span className="iv-card-top">
                    <span className="iv-no">{inv.invoice_number}</span>
                    {status(inv.status)}
                  </span>
                  {!scoped && (
                    <span className="iv-card-who">{inv.contractor_name || dash}
                      {inv.company && <small> · {inv.company}</small>}</span>
                  )}
                  <span className="iv-card-figs">
                    <span><small>Date</small>{day(inv.invoice_date)}</span>
                    <span><small>Billed</small>{hours(inv.hours_billed)}</span>
                    <span><small>Budget</small>{maybeH(inv.budget_hours)}</span>
                    <span><small>Amount</small><strong>{money(inv.amount)}</strong></span>
                  </span>
                  <span className="iv-card-more">
                    {isOpen ? 'Hide' : 'Show'} {inv.ticket_count} ticket{inv.ticket_count === 1 ? '' : 's'}
                    {inv.over_budget > 0 && <span className="iv-over-chip">{inv.over_budget} over budget</span>}
                    <svg className="iv-chev" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M6 4 10 8 6 12" />
                    </svg>
                  </span>
                </button>
                <div className="iv-collapse" id={id} role="region" aria-label={`Tickets on ${inv.invoice_number}`}
                     {...(isOpen ? {} : { inert: '' })}>
                  <div className="iv-collapse-inner">{(isOpen || lines[inv.key]) && linesFor(inv)}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {pages > 1 && (
        <nav className="iv-pages" aria-label="Pages">
          <button type="button" className="iv-btn" disabled={view.page <= 1}
                  onClick={() => update({ page: view.page - 1 }, { keepPage: true })}>← Previous</button>
          <span>Page {view.page} of {pages} · {data?.total} invoices</span>
          <button type="button" className="iv-btn" disabled={view.page >= pages}
                  onClick={() => update({ page: view.page + 1 }, { keepPage: true })}>Next →</button>
        </nav>
      )}
    </div>
  );
}
